'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { HOME_FAQ_KEYS } from '@/lib/home-faq';

/**
 * Six answered questions on the homepage, mirrored exactly by the FAQPage
 * JSON-LD that app/(routes)/page.tsx emits from the same HOME_FAQ_KEYS.
 *
 * Answers are rendered OPEN, not in an accordion. /faq uses an accordion and
 * keeps every answer in the DOM behind max-h-0, which is fine there because the
 * whole page is the FAQ. Here the point is that an answer engine lifting a
 * passage off the homepage gets the full answer with no interaction and no
 * "is this hidden content?" ambiguity, so the answers are simply visible.
 *
 * Questions are h3: the section heading is the h2, and the hero owns the h1.
 * globals.css applies var(--font-playfair) to h1-h6, so the body font is
 * restored on each question to match how /faq renders the same strings.
 */
const HomeFaq: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useIntersectionObserver();

  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="container">
        <div ref={ref} className={`max-w-3xl mx-auto fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pageContent.home.faq.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pageContent.home.faq.subtitle')}
            </p>
          </div>

          <dl className="space-y-8">
            {HOME_FAQ_KEYS.map((key) => (
              <div key={key} className="border-b border-gray-100 pb-8 last:border-b-0 last:pb-0">
                <dt>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 font-[family-name:var(--font-inter)]">
                    {t(`pageContent.faq.questions.${key}.q`)}
                  </h3>
                </dt>
                <dd className="text-gray-600 leading-relaxed">
                  {t(`pageContent.faq.questions.${key}.a`)}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-10 text-center">
            <Link
              href="/faq"
              className="inline-flex items-center font-semibold text-primary-600 hover:text-primary-700 transition-colors group"
            >
              {t('pageContent.home.faq.moreLink')}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export { HomeFaq };
